import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { 
  Warehouse, Package, ArrowDownToLine, ArrowUpFromLine, Calculator,
  Plus, Clock, CheckCircle, TrendingDown, DollarSign, Filter,
  Calendar, ClipboardCheck, BarChart3, Save
} from 'lucide-react';
import { getErrorMessage } from '../lib/utils';

import { API } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';


const getItemTypeBadge = (type) => {
  if (type === 'price_master') {
    return <Badge className="bg-green-100 text-green-800">Consumable</Badge>;
  }
  return <Badge className="bg-indigo-100 text-indigo-800">Non-Consumable</Badge>;
};

const getSuperCategoryColor = (sc) => {
  const colors = {
    "Liquor": "bg-purple-100 text-purple-800",
    "Food": "bg-orange-100 text-orange-800",
    "Normal-Drinks": "bg-amber-100 text-amber-800",
    "NA-Kitchen": "bg-orange-100 text-orange-800",
    "NA-Cafe": "bg-amber-100 text-amber-800",
    "NA-Bar": "bg-purple-100 text-purple-800",
    "NA-Service": "bg-blue-100 text-blue-800",
    "NA-Housekeeping": "bg-green-100 text-green-800",
    "NA-Packaging": "bg-pink-100 text-pink-800"
  };
  return colors[sc] || "bg-gray-100 text-gray-800";
};

export default function StoreLedger({ user }) {
  const [activeTab, setActiveTab] = useState('daily');
  const [loading, setLoading] = useState(true);
  
  // Data
  const [allItems, setAllItems] = useState({ price_master_items: [], na_price_master_items: [] });
  const [openingStocks, setOpeningStocks] = useState([]);
  const [dailyLedger, setDailyLedger] = useState({ ledgers: [], summary: {} });
  const [inStockTransactions, setInStockTransactions] = useState([]);
  const [issueTransactions, setIssueTransactions] = useState([]);
  const [dailyClosings, setDailyClosings] = useState([]);
  const [naReport, setNaReport] = useState({ items: [], category_summary: [], grand_total: {} });
  const [assetRegister, setAssetRegister] = useState({ assets: [], by_department: [], grand_total: {} });
  const [consumableReport, setConsumableReport] = useState({ items: [], department_summary: [], category_summary: [], grand_total: {} });
  const [summary, setSummary] = useState({});
  
  // Filters
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
  const [filterItemType, setFilterItemType] = useState('all');
  const [filterSuperCategory, setFilterSuperCategory] = useState('all');
  const [reportDateRange, setReportDateRange] = useState({
    start: new Date(new Date().setDate(1)).toISOString().slice(0, 10),
    end: new Date().toISOString().slice(0, 10)
  });
  const [reportSortBy, setReportSortBy] = useState('value_high');
  const [assetDepartment, setAssetDepartment] = useState('all');
  const [consumableDepartment, setConsumableDepartment] = useState('all');
  const [consumableStatus, setConsumableStatus] = useState('all');
  
  // Dialogs
  const [showAddOpening, setShowAddOpening] = useState(false);
  const [showAddInStock, setShowAddInStock] = useState(false);
  const [showAddIssue, setShowAddIssue] = useState(false);
  
  // Forms
  const [openingForm, setOpeningForm] = useState({ item_id: '', item_type: '', opening_stock: '', date: selectedDate });
  const [inStockForm, setInStockForm] = useState({ item_id: '', item_type: '', quantity: '', unit_price: '', remarks: '' });
  const [issueForm, setIssueForm] = useState({ item_id: '', item_type: '', quantity: '', issued_to: '', remarks: '' });
  
  const isAdmin = user?.role === 'admin';
  const isStorekeeper = user?.role === 'storekeeper';
  const canManage = isAdmin || isStorekeeper;

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (activeTab === 'daily' || activeTab === 'closing') {
      fetchDailyLedger();
    } else if (activeTab === 'opening') {
      fetchOpeningStocks();
    } else if (activeTab === 'in-stock') {
      fetchInStockTransactions();
    } else if (activeTab === 'issue') {
      fetchIssueTransactions();
    } else if (activeTab === 'history') {
      fetchDailyClosings();
    } else if (activeTab === 'na-report') {
      fetchNAReport();
    } else if (activeTab === 'asset-register') {
      fetchAssetRegister();
    } else if (activeTab === 'consumable-report') {
      fetchConsumableReport();
    }
  }, [activeTab, selectedDate, filterItemType, filterSuperCategory, reportDateRange, reportSortBy, assetDepartment, consumableDepartment, consumableStatus]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const [itemsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/store-ledger/all-items`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/store-ledger/summary`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setAllItems(itemsRes.data);
      setSummary(summaryRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Store Ledger fetch error:', error);
      toast.error(error.message || 'Failed to fetch data', {
        action: {
          label: 'Retry',
          onClick: () => fetchData()
        }
      });
      setLoading(false);
    }
  };

  const fetchOpeningStocks = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (filterItemType !== 'all') params.item_type = filterItemType;
      if (filterSuperCategory !== 'all') params.super_category = filterSuperCategory;
      
      const response = await axios.get(`${API}/store-ledger/opening-stock`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setOpeningStocks(response.data);
    } catch (error) {
      toast.error('Failed to fetch opening stocks');
    }
  };

  const fetchDailyLedger = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = { date: selectedDate };
      if (filterItemType !== 'all') params.item_type = filterItemType;
      if (filterSuperCategory !== 'all') params.super_category = filterSuperCategory;
      
      const response = await axios.get(`${API}/store-ledger/daily`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setDailyLedger(response.data);
    } catch (error) {
      console.error('Failed to fetch daily ledger:', error);
    }
  };

  const fetchInStockTransactions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/store-ledger/in-stock`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { date: selectedDate }
      });
      setInStockTransactions(response.data);
    } catch (error) {
      toast.error('Failed to fetch in-stock transactions');
    }
  };

  const fetchIssueTransactions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/store-ledger/issue-stock`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { date: selectedDate }
      });
      setIssueTransactions(response.data);
    } catch (error) {
      toast.error('Failed to fetch issue transactions');
    }
  };

  const fetchDailyClosings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/store-ledger/daily-closings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDailyClosings(response.data);
    } catch (error) {
      toast.error('Failed to fetch daily closings');
    }
  };

  const fetchNAReport = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = {
        start_date: reportDateRange.start,
        end_date: reportDateRange.end,
        sort_by: reportSortBy
      };
      if (filterSuperCategory !== 'all' && filterSuperCategory.startsWith('NA-')) {
        params.super_category = filterSuperCategory;
      }
      
      const response = await axios.get(`${API}/store-ledger/na-report`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setNaReport(response.data);
    } catch (error) {
      toast.error('Failed to fetch NA report');
    }
  };

  const fetchAssetRegister = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = {
        start_date: reportDateRange.start,
        end_date: reportDateRange.end
      };
      if (assetDepartment !== 'all') {
        params.department = assetDepartment;
      }
      
      const response = await axios.get(`${API}/store-ledger/asset-register`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setAssetRegister(response.data);
    } catch (error) {
      toast.error('Failed to fetch asset register');
    }
  };

  const fetchConsumableReport = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = {
        start_date: reportDateRange.start,
        end_date: reportDateRange.end,
        sort_by: reportSortBy
      };
      if (consumableDepartment !== 'all') {
        params.department = consumableDepartment;
      }
      if (consumableStatus !== 'all') {
        params.status = consumableStatus;
      }
      
      const response = await axios.get(`${API}/store-ledger/consumable-report`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setConsumableReport(response.data);
    } catch (error) {
      toast.error('Failed to fetch consumable report');
    }
  };

  const handleSetOpening = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/store-ledger/opening-stock`, {
        ...openingForm,
        opening_stock: parseFloat(openingForm.opening_stock),
        date: openingForm.date || selectedDate
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Opening stock set');
      setShowAddOpening(false);
      setOpeningForm({ item_id: '', item_type: '', opening_stock: '', date: selectedDate });
      fetchOpeningStocks();
      fetchInitialData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to set opening stock'));
    }
  };

  const handleAddInStock = async () => {
    try {
      const token = localStorage.getItem('token');
      const payload = {
        date: selectedDate,
        item_id: inStockForm.item_id,
        item_type: inStockForm.item_type,
        quantity: parseFloat(inStockForm.quantity),
        remarks: inStockForm.remarks
      };
      
      // Include unit_price if provided (auto-updates price master)
      if (inStockForm.unit_price && parseFloat(inStockForm.unit_price) > 0) {
        payload.unit_price = parseFloat(inStockForm.unit_price);
      }
      
      const response = await axios.post(`${API}/store-ledger/in-stock`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      setShowAddInStock(false);
      setInStockForm({ item_id: '', item_type: '', quantity: '', unit_price: '', remarks: '' });
      fetchInStockTransactions();
      fetchDailyLedger();
      fetchInitialData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to add in-stock'));
    }
  };

  const handleAddIssue = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/store-ledger/issue-stock`, {
        date: selectedDate,
        item_id: issueForm.item_id,
        item_type: issueForm.item_type,
        quantity: parseFloat(issueForm.quantity),
        issued_to: issueForm.issued_to,
        remarks: issueForm.remarks
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Show message about acknowledgment flow
      const item = [...allItems.price_master_items, ...allItems.na_price_master_items].find(i => i.id === issueForm.item_id);
      if (item && issueForm.item_type === 'price_master') {
        const dest = item.super_category === 'Liquor' ? 'Bar' : item.super_category === 'Food' ? 'Kitchen' : 'Cafe';
        toast.success(`Issue stock added! ${dest} team will acknowledge to add to their ledger.`);
      } else if (issueForm.issued_to) {
        toast.success(`Issue stock added! Tracked in ${issueForm.issued_to} Asset Register.`);
      } else {
        toast.success('Issue stock added');
      }
      
      setShowAddIssue(false);
      setIssueForm({ item_id: '', item_type: '', quantity: '', issued_to: '', remarks: '' });
      fetchIssueTransactions();
      fetchDailyLedger();
      fetchInitialData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to add issue stock'));
    }
  };

  const handleSubmitDaily = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/store-ledger/daily/submit`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: { date: selectedDate }
      });
      toast.success('Daily ledger submitted');
      fetchDailyLedger();
      fetchInitialData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to submit daily ledger'));
    }
  };

  const getAllItemsList = () => {
    return [
      ...allItems.price_master_items.map(i => ({ ...i, item_type: 'price_master' })),
      ...allItems.na_price_master_items.map(i => ({ ...i, item_type: 'na_price_master' }))
    ];
  };

  const canSubmitDaily = dailyLedger.ledgers?.length > 0 && summary.today_status !== 'submitted';

  return (
    <div className="space-y-6" data-testid="store-ledger-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Warehouse className="w-6 h-6 text-teal-600" />
            Store Ledger
          </h1>
          <p className="text-gray-600">Central inventory management</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-teal-50 border-teal-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-1">
              <Package className="w-4 h-4 text-teal-600" />
              <span className="text-sm text-teal-600 font-medium">Items Setup</span>
            </div>
            <p className="text-2xl font-bold text-teal-700">{summary.items_with_opening_stock || 0}</p>
          </CardContent>
        </Card>
        
        <Card className="bg-green-50 border-green-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-1">
              <ArrowDownToLine className="w-4 h-4 text-green-600" />
              <span className="text-sm text-green-600 font-medium">Today In-Stock</span>
            </div>
            <p className="text-2xl font-bold text-green-700">{summary.today_in_stock_count || 0}</p>
          </CardContent>
        </Card>
        
        <Card className="bg-red-50 border-red-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-1">
              <ArrowUpFromLine className="w-4 h-4 text-red-600" />
              <span className="text-sm text-red-600 font-medium">Today Issued</span>
            </div>
            <p className="text-2xl font-bold text-red-700">{summary.today_issue_count || 0}</p>
          </CardContent>
        </Card>
        
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-1">
              <DollarSign className="w-4 h-4 text-blue-600" />
              <span className="text-sm text-blue-600 font-medium">Month In-Value</span>
            </div>
            <p className="text-lg font-bold text-blue-700">{formatCurrency(summary.month_in_value)}</p>
          </CardContent>
        </Card>
        
        <Card className="bg-orange-50 border-orange-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-1">
              <TrendingDown className="w-4 h-4 text-orange-600" />
              <span className="text-sm text-orange-600 font-medium">Month Issue-Value</span>
            </div>
            <p className="text-lg font-bold text-orange-700">{formatCurrency(summary.month_issue_value)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex-wrap">
          <TabsTrigger value="daily">Daily Ledger</TabsTrigger>
          <TabsTrigger value="opening">Opening Stock</TabsTrigger>
          <TabsTrigger value="in-stock">In-Stock</TabsTrigger>
          <TabsTrigger value="issue">Issue Stock</TabsTrigger>
          <TabsTrigger value="closing">Closing Stock</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
          <TabsTrigger value="consumable-report">Consumable Report</TabsTrigger>
          {isAdmin && <TabsTrigger value="na-report">NA Report</TabsTrigger>}
          <TabsTrigger value="asset-register">Asset Register</TabsTrigger>
        </TabsList>

        {/* Daily Ledger Tab */}
        <TabsContent value="daily" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex flex-wrap justify-between items-center gap-4">
                <div className="flex items-center gap-4">
                  <CardTitle>Daily Ledger</CardTitle>
                  <Input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="w-44" />
                  <Badge className={summary.today_status === 'submitted' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                    {summary.today_status === 'submitted' ? 'Submitted' : 'Draft'}
                  </Badge>
                </div>
                <div className="flex gap-2">
                  {canManage && canSubmitDaily && (
                    <Button onClick={handleSubmitDaily}>
                      <ClipboardCheck className="w-4 h-4 mr-2" />
                      Submit Daily
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {dailyLedger.ledgers?.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Warehouse className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                  <p>No transactions for this date</p>
                  <p className="text-sm mt-1">Add in-stock or issue transactions to see ledger</p>
                </div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Item</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead className="text-right">Opening</TableHead>
                        <TableHead className="text-right">In</TableHead>
                        <TableHead className="text-right">Issue</TableHead>
                        <TableHead className="text-right">Closing</TableHead>
                        <TableHead className="text-right">Closing Value</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dailyLedger.ledgers?.map((ledger) => (
                        <TableRow key={ledger.id}>
                          <TableCell className="font-medium">{ledger.item_name}</TableCell>
                          <TableCell>{getItemTypeBadge(ledger.item_type)}</TableCell>
                          <TableCell><Badge className={getSuperCategoryColor(ledger.super_category)}>{ledger.super_category}</Badge></TableCell>
                          <TableCell className="text-right">{ledger.opening_stock?.toFixed(2)}</TableCell>
                          <TableCell className="text-right text-green-600">+{ledger.in_stock?.toFixed(2)}</TableCell>
                          <TableCell className="text-right text-red-600">-{ledger.issue_stock?.toFixed(2)}</TableCell>
                          <TableCell className="text-right font-bold">{ledger.closing_stock?.toFixed(2)}</TableCell>
                          <TableCell className="text-right font-bold">{formatCurrency(ledger.closing_value)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  
                  {dailyLedger.summary && (
                    <Card className="mt-4 bg-gray-50">
                      <CardContent className="pt-4">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                          <div>
                            <p className="text-sm text-gray-600">Opening Value</p>
                            <p className="text-lg font-bold">{formatCurrency(dailyLedger.summary.total_opening_value)}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">In Value</p>
                            <p className="text-lg font-bold text-green-600">+{formatCurrency(dailyLedger.summary.total_in_value)}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Issue Value</p>
                            <p className="text-lg font-bold text-red-600">-{formatCurrency(dailyLedger.summary.total_issue_value)}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Closing Value</p>
                            <p className="text-lg font-bold text-blue-600">{formatCurrency(dailyLedger.summary.total_closing_value)}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Opening Stock Tab */}
        <TabsContent value="opening" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Opening Stock Setup</CardTitle>
                {canManage && (
                  <Button onClick={() => setShowAddOpening(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Set Opening Stock
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {openingStocks.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No opening stocks set yet</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Unit</TableHead>
                      <TableHead className="text-right">Opening Stock</TableHead>
                      <TableHead className="text-right">Opening Value</TableHead>
                      <TableHead>Start Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {openingStocks.map((stock) => (
                      <TableRow key={stock.id}>
                        <TableCell className="font-medium">{stock.item_name}</TableCell>
                        <TableCell>{getItemTypeBadge(stock.item_type)}</TableCell>
                        <TableCell><Badge className={getSuperCategoryColor(stock.super_category)}>{stock.super_category}</Badge></TableCell>
                        <TableCell>{stock.unit}</TableCell>
                        <TableCell className="text-right">{stock.opening_stock?.toFixed(2)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(stock.opening_value)}</TableCell>
                        <TableCell>{stock.start_date}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* In-Stock Tab */}
        <TabsContent value="in-stock" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-4">
                  <CardTitle>In-Stock (Received)</CardTitle>
                  <Input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="w-44" />
                </div>
                {canManage && (
                  <Button onClick={() => setShowAddInStock(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add In-Stock
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {inStockTransactions.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No in-stock transactions for this date</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-right">Quantity</TableHead>
                      <TableHead className="text-right">Value</TableHead>
                      <TableHead>Remarks</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {inStockTransactions.map((tx) => (
                      <TableRow key={tx.id}>
                        <TableCell className="font-medium">{tx.item_name}</TableCell>
                        <TableCell>{getItemTypeBadge(tx.item_type)}</TableCell>
                        <TableCell><Badge className={getSuperCategoryColor(tx.super_category)}>{tx.super_category}</Badge></TableCell>
                        <TableCell className="text-right text-green-600">+{tx.quantity?.toFixed(2)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(tx.total_value)}</TableCell>
                        <TableCell>{tx.remarks}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Issue Stock Tab */}
        <TabsContent value="issue" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-4">
                  <CardTitle>Issue Stock (Issued Out)</CardTitle>
                  <Input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="w-44" />
                </div>
                {canManage && (
                  <Button onClick={() => setShowAddIssue(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Issue Stock
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="mb-4 p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
                <strong>Acknowledgment Flow:</strong> Consumable items (Liquor → Bar, Food → Kitchen, Normal-Drinks → Cafe) require acknowledgment from the receiving department before being added to their ledger. NA-items are tracked in the Asset Register.
              </div>
              {issueTransactions.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No issue transactions for this date</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-right">Quantity</TableHead>
                      <TableHead className="text-right">Value</TableHead>
                      <TableHead>Issued To</TableHead>
                      <TableHead>Remarks</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {issueTransactions.map((tx) => (
                      <TableRow key={tx.id}>
                        <TableCell className="font-medium">{tx.item_name}</TableCell>
                        <TableCell>{getItemTypeBadge(tx.item_type)}</TableCell>
                        <TableCell><Badge className={getSuperCategoryColor(tx.super_category)}>{tx.super_category}</Badge></TableCell>
                        <TableCell className="text-right text-red-600">-{tx.quantity?.toFixed(2)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(tx.total_value)}</TableCell>
                        <TableCell>{tx.issued_to}</TableCell>
                        <TableCell>{tx.remarks}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Closing Stock Tab */}
        <TabsContent value="closing" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-4">
                <CardTitle>Closing Stock (Auto-Calculated)</CardTitle>
                <Input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="w-44" />
              </div>
            </CardHeader>
            <CardContent>
              {dailyLedger.ledgers?.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No closing stock data for this date</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Unit</TableHead>
                      <TableHead className="text-right">Closing Stock</TableHead>
                      <TableHead className="text-right">Unit Price</TableHead>
                      <TableHead className="text-right">Closing Value</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dailyLedger.ledgers?.map((ledger) => (
                      <TableRow key={ledger.id}>
                        <TableCell className="font-medium">{ledger.item_name}</TableCell>
                        <TableCell>{getItemTypeBadge(ledger.item_type)}</TableCell>
                        <TableCell><Badge className={getSuperCategoryColor(ledger.super_category)}>{ledger.super_category}</Badge></TableCell>
                        <TableCell>{ledger.unit}</TableCell>
                        <TableCell className="text-right font-bold">{ledger.closing_stock?.toFixed(2)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(ledger.unit_price)}</TableCell>
                        <TableCell className="text-right font-bold text-blue-600">{formatCurrency(ledger.closing_value)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-gray-100 font-bold">
                      <TableCell colSpan={6}>TOTAL CLOSING VALUE</TableCell>
                      <TableCell className="text-right text-blue-700">{formatCurrency(dailyLedger.summary?.total_closing_value)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Daily Submission History</CardTitle>
            </CardHeader>
            <CardContent>
              {dailyClosings.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No submissions yet</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-center">Items</TableHead>
                      <TableHead className="text-right">Opening Value</TableHead>
                      <TableHead className="text-right">In Value</TableHead>
                      <TableHead className="text-right">Issue Value</TableHead>
                      <TableHead className="text-right">Closing Value</TableHead>
                      <TableHead>Submitted By</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dailyClosings.map((closing) => (
                      <TableRow key={closing.id}>
                        <TableCell className="font-medium">{closing.date}</TableCell>
                        <TableCell className="text-center">{closing.total_items}</TableCell>
                        <TableCell className="text-right">{formatCurrency(closing.total_opening_value)}</TableCell>
                        <TableCell className="text-right text-green-600">{formatCurrency(closing.total_in_value)}</TableCell>
                        <TableCell className="text-right text-red-600">{formatCurrency(closing.total_issue_value)}</TableCell>
                        <TableCell className="text-right font-bold">{formatCurrency(closing.total_closing_value)}</TableCell>
                        <TableCell>{closing.submitted_by_name}</TableCell>
                        <TableCell><Badge className="bg-green-100 text-green-800">Submitted</Badge></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Consumable Items Report Tab */}
        <TabsContent value="consumable-report" className="mt-4">
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex flex-wrap justify-between items-center gap-4">
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5" />
                    Consumable Items Issue Report
                  </CardTitle>
                  <div className="flex flex-wrap gap-3 items-center">
                    <Input type="date" value={reportDateRange.start} onChange={(e) => setReportDateRange(p => ({ ...p, start: e.target.value }))} className="w-40" />
                    <span>to</span>
                    <Input type="date" value={reportDateRange.end} onChange={(e) => setReportDateRange(p => ({ ...p, end: e.target.value }))} className="w-40" />
                    <Select value={consumableDepartment} onValueChange={setConsumableDepartment}>
                      <SelectTrigger className="w-36"><SelectValue placeholder="Department" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Departments</SelectItem>
                        <SelectItem value="bar">Bar</SelectItem>
                        <SelectItem value="kitchen">Kitchen</SelectItem>
                        <SelectItem value="cafe">Cafe</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={consumableStatus} onValueChange={setConsumableStatus}>
                      <SelectTrigger className="w-40"><SelectValue placeholder="Status" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="acknowledged">Acknowledged</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={reportSortBy} onValueChange={setReportSortBy}>
                      <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="value_high">Value: High to Low</SelectItem>
                        <SelectItem value="value_low">Value: Low to High</SelectItem>
                        <SelectItem value="quantity_high">Qty: High to Low</SelectItem>
                        <SelectItem value="quantity_low">Qty: Low to High</SelectItem>
                        <SelectItem value="date_new">Date: Newest</SelectItem>
                        <SelectItem value="date_old">Date: Oldest</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Department Summary */}
                {consumableReport.department_summary?.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    {consumableReport.department_summary.map((dept) => (
                      <Card key={dept.department} className={`border-2 ${dept.department === 'bar' ? 'border-purple-200 bg-purple-50' : dept.department === 'kitchen' ? 'border-orange-200 bg-orange-50' : 'border-amber-200 bg-amber-50'}`}>
                        <CardContent className="pt-4">
                          <div className="text-sm font-medium capitalize">{dept.department_name}</div>
                          <p className="text-xl font-bold">{formatCurrency(dept.total_value)}</p>
                          <div className="flex gap-2 mt-2 text-xs">
                            <Badge variant="outline" className="bg-yellow-100">{dept.pending_count} pending</Badge>
                            <Badge variant="outline" className="bg-green-100">{dept.acknowledged_count} ack</Badge>
                            <Badge variant="outline" className="bg-red-100">{dept.rejected_count} rejected</Badge>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
                
                {/* Grand Total */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6 p-4 bg-gray-100 rounded-lg">
                  <div>
                    <p className="text-xs text-gray-500">Total Items</p>
                    <p className="text-lg font-bold">{consumableReport.grand_total?.items || 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Total Value</p>
                    <p className="text-lg font-bold text-green-700">{formatCurrency(consumableReport.grand_total?.value)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Pending</p>
                    <p className="text-lg font-bold text-yellow-600">{consumableReport.grand_total?.pending || 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Acknowledged</p>
                    <p className="text-lg font-bold text-green-600">{consumableReport.grand_total?.acknowledged || 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Rejected</p>
                    <p className="text-lg font-bold text-red-600">{consumableReport.grand_total?.rejected || 0}</p>
                  </div>
                </div>
                
                {consumableReport.items?.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <BarChart3 className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                    <p>No consumable items issued in selected period</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Item</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Department</TableHead>
                        <TableHead className="text-right">Qty</TableHead>
                        <TableHead className="text-right">Price</TableHead>
                        <TableHead className="text-right">Value</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Issued By</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {consumableReport.items?.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{item.date}</TableCell>
                          <TableCell className="font-medium">{item.item_name}</TableCell>
                          <TableCell><Badge variant="outline">{item.super_category}</Badge></TableCell>
                          <TableCell><Badge className={item.department === 'bar' ? 'bg-purple-100 text-purple-800' : item.department === 'kitchen' ? 'bg-orange-100 text-orange-800' : 'bg-amber-100 text-amber-800'}>{item.department_name}</Badge></TableCell>
                          <TableCell className="text-right">{item.quantity?.toFixed(2)} {item.unit}</TableCell>
                          <TableCell className="text-right">{formatCurrency(item.unit_price)}</TableCell>
                          <TableCell className="text-right font-bold">{formatCurrency(item.total_value)}</TableCell>
                          <TableCell>
                            <Badge className={item.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : item.status === 'acknowledged' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                              {item.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-gray-600">{item.issued_by_name}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* NA Report Tab (Admin Only) */}
        {isAdmin && (
          <TabsContent value="na-report" className="mt-4">
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <div className="flex flex-wrap justify-between items-center gap-4">
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="w-5 h-5" />
                      NA Items Issue Report
                    </CardTitle>
                    <div className="flex flex-wrap gap-3 items-center">
                      <Input type="date" value={reportDateRange.start} onChange={(e) => setReportDateRange(p => ({ ...p, start: e.target.value }))} className="w-40" />
                      <span>to</span>
                      <Input type="date" value={reportDateRange.end} onChange={(e) => setReportDateRange(p => ({ ...p, end: e.target.value }))} className="w-40" />
                      <Select value={filterSuperCategory} onValueChange={setFilterSuperCategory}>
                        <SelectTrigger className="w-44"><SelectValue placeholder="All NA Categories" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All NA Categories</SelectItem>
                          <SelectItem value="NA-Kitchen">NA-Kitchen</SelectItem>
                          <SelectItem value="NA-Cafe">NA-Cafe</SelectItem>
                          <SelectItem value="NA-Bar">NA-Bar</SelectItem>
                          <SelectItem value="NA-Service">NA-Service</SelectItem>
                          <SelectItem value="NA-Housekeeping">NA-Housekeeping</SelectItem>
                          <SelectItem value="NA-Packaging">NA-Packaging</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select value={reportSortBy} onValueChange={setReportSortBy}>
                        <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="value_high">Value: High to Low</SelectItem>
                          <SelectItem value="value_low">Value: Low to High</SelectItem>
                          <SelectItem value="quantity_high">Qty: High to Low</SelectItem>
                          <SelectItem value="quantity_low">Qty: Low to High</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardHeader>
              </Card>

              {/* Category Summary */}
              {naReport.category_summary?.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  {naReport.category_summary.map((cat, idx) => (
                    <Card key={idx} className="bg-gray-50">
                      <CardContent className="pt-4">
                        <Badge className={getSuperCategoryColor(cat.super_category)}>{cat.super_category}</Badge>
                        <p className="text-xl font-bold mt-2">{formatCurrency(cat.total_value)}</p>
                        <p className="text-sm text-gray-600">{cat.items_count} items, {cat.total_quantity?.toFixed(2)} units</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {/* Items Table */}
              <Card>
                <CardContent className="pt-4">
                  {naReport.items?.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">No NA items issued in this period</div>
                  ) : (
                    <>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Item</TableHead>
                            <TableHead>Category</TableHead>
                            <TableHead>Company</TableHead>
                            <TableHead>Unit</TableHead>
                            <TableHead className="text-center">Transactions</TableHead>
                            <TableHead className="text-right">Total Qty</TableHead>
                            <TableHead className="text-right">Total Value</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {naReport.items?.map((item, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="font-medium">{item.item_name}</TableCell>
                              <TableCell><Badge className={getSuperCategoryColor(item.super_category)}>{item.super_category}</Badge></TableCell>
                              <TableCell>{item.company}</TableCell>
                              <TableCell>{item.unit}</TableCell>
                              <TableCell className="text-center">{item.transaction_count}</TableCell>
                              <TableCell className="text-right">{item.total_quantity?.toFixed(2)}</TableCell>
                              <TableCell className="text-right font-bold">{formatCurrency(item.total_value)}</TableCell>
                            </TableRow>
                          ))}
                          <TableRow className="bg-gray-100 font-bold">
                            <TableCell colSpan={5}>GRAND TOTAL</TableCell>
                            <TableCell className="text-right">{naReport.grand_total?.quantity?.toFixed(2)}</TableCell>
                            <TableCell className="text-right text-blue-700">{formatCurrency(naReport.grand_total?.value)}</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        )}

        {/* Asset Register Tab */}
        <TabsContent value="asset-register" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex flex-wrap justify-between items-center gap-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Warehouse className="w-5 h-5" />
                    Asset Register
                  </CardTitle>
                  <p className="text-sm text-gray-500 mt-1">Non-consumable assets issued to departments</p>
                </div>
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Label>From:</Label>
                    <Input type="date" value={reportDateRange.start} onChange={(e) => setReportDateRange({ ...reportDateRange, start: e.target.value })} className="w-40" />
                  </div>
                  <div className="flex items-center gap-2">
                    <Label>To:</Label>
                    <Input type="date" value={reportDateRange.end} onChange={(e) => setReportDateRange({ ...reportDateRange, end: e.target.value })} className="w-40" />
                  </div>
                  <Select value={assetDepartment} onValueChange={setAssetDepartment}>
                    <SelectTrigger className="w-40"><SelectValue placeholder="Department" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Departments</SelectItem>
                      <SelectItem value="NA-Kitchen">NA-Kitchen</SelectItem>
                      <SelectItem value="NA-Bar">NA-Bar</SelectItem>
                      <SelectItem value="NA-Cafe">NA-Cafe</SelectItem>
                      <SelectItem value="NA-Service">NA-Service</SelectItem>
                      <SelectItem value="NA-Housekeeping">NA-Housekeeping</SelectItem>
                      <SelectItem value="NA-Packaging">NA-Packaging</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Summary by Department */}
              {assetRegister.by_department?.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  {assetRegister.by_department.map((dept) => (
                    <Card key={dept.department} className="bg-indigo-50 border-indigo-200">
                      <CardContent className="pt-4">
                        <div className="text-sm font-medium text-indigo-600">{dept.department}</div>
                        <p className="text-lg font-bold text-indigo-700">{formatCurrency(dept.total_value)}</p>
                        <p className="text-xs text-gray-500">{dept.total_quantity?.toFixed(2)} units</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
              
              {assetRegister.assets?.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Warehouse className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                  <p>No assets in register for selected period</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Item</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Department</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Unit Price</TableHead>
                      <TableHead className="text-right">Value</TableHead>
                      <TableHead>Issued By</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {assetRegister.assets?.map((asset) => (
                      <TableRow key={asset.id}>
                        <TableCell>{asset.date}</TableCell>
                        <TableCell className="font-medium">{asset.item_name}</TableCell>
                        <TableCell><Badge className={getSuperCategoryColor(asset.super_category)}>{asset.super_category}</Badge></TableCell>
                        <TableCell><Badge variant="outline">{asset.department}</Badge></TableCell>
                        <TableCell className="text-right">{asset.quantity?.toFixed(2)} {asset.unit}</TableCell>
                        <TableCell className="text-right">{formatCurrency(asset.unit_price)}</TableCell>
                        <TableCell className="text-right font-bold">{formatCurrency(asset.total_value)}</TableCell>
                        <TableCell className="text-sm text-gray-600">{asset.issued_by_name}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-gray-100 font-bold">
                      <TableCell colSpan={4}>GRAND TOTAL</TableCell>
                      <TableCell className="text-right">{assetRegister.grand_total?.quantity?.toFixed(2)}</TableCell>
                      <TableCell></TableCell>
                      <TableCell className="text-right text-indigo-700">{formatCurrency(assetRegister.grand_total?.value)}</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Opening Stock Dialog */}
      <Dialog open={showAddOpening} onOpenChange={setShowAddOpening}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set Opening Stock</DialogTitle>
            <DialogDescription>Set initial stock for an item</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Select Item</Label>
              <Select value={`${openingForm.item_type}|${openingForm.item_id}`} onValueChange={(v) => {
                const [type, id] = v.split('|');
                setOpeningForm({ ...openingForm, item_type: type, item_id: id });
              }}>
                <SelectTrigger><SelectValue placeholder="Select item" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_header_consumables" disabled>-- Consumables --</SelectItem>
                  {allItems.price_master_items.map(i => (
                    <SelectItem key={i.id} value={`price_master|${i.id}`}>{i.item_name} ({i.super_category})</SelectItem>
                  ))}
                  <SelectItem value="_header_na" disabled>-- Non-Consumables --</SelectItem>
                  {allItems.na_price_master_items.map(i => (
                    <SelectItem key={i.id} value={`na_price_master|${i.id}`}>{i.item_name} ({i.super_category})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Opening Stock Quantity</Label>
              <Input type="number" step="0.01" value={openingForm.opening_stock} onChange={(e) => setOpeningForm({ ...openingForm, opening_stock: e.target.value })} />
            </div>
            <div>
              <Label>Start Date</Label>
              <Input type="date" value={openingForm.date} onChange={(e) => setOpeningForm({ ...openingForm, date: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddOpening(false)}>Cancel</Button>
            <Button onClick={handleSetOpening} disabled={!openingForm.item_id || !openingForm.opening_stock}>Set Opening</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add In-Stock Dialog */}
      <Dialog open={showAddInStock} onOpenChange={setShowAddInStock}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add In-Stock</DialogTitle>
            <DialogDescription>Record stock received for {selectedDate}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Select Item</Label>
              <Select value={`${inStockForm.item_type}|${inStockForm.item_id}`} onValueChange={(v) => {
                const [type, id] = v.split('|');
                setInStockForm({ ...inStockForm, item_type: type, item_id: id });
              }}>
                <SelectTrigger><SelectValue placeholder="Select item" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_header_consumables" disabled>-- Consumables --</SelectItem>
                  {allItems.price_master_items.map(i => (
                    <SelectItem key={i.id} value={`price_master|${i.id}`}>{i.item_name} ({i.super_category})</SelectItem>
                  ))}
                  <SelectItem value="_header_na" disabled>-- Non-Consumables --</SelectItem>
                  {allItems.na_price_master_items.map(i => (
                    <SelectItem key={i.id} value={`na_price_master|${i.id}`}>{i.item_name} ({i.super_category})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Quantity</Label>
              <Input type="number" step="0.01" value={inStockForm.quantity} onChange={(e) => setInStockForm({ ...inStockForm, quantity: e.target.value })} />
            </div>
            <div>
              <Label>Unit Price (optional - auto-updates Price Master)</Label>
              <Input 
                type="number" 
                step="0.01" 
                value={inStockForm.unit_price} 
                onChange={(e) => setInStockForm({ ...inStockForm, unit_price: e.target.value })}
                placeholder="Leave empty to use existing price"
              />
              <p className="text-xs text-gray-500 mt-1">If entered, this price will update Price Master for current month</p>
            </div>
            <div>
              <Label>Remarks (optional)</Label>
              <Input value={inStockForm.remarks} onChange={(e) => setInStockForm({ ...inStockForm, remarks: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddInStock(false)}>Cancel</Button>
            <Button onClick={handleAddInStock} disabled={!inStockForm.item_id || !inStockForm.quantity}>Add In-Stock</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Issue Stock Dialog */}
      <Dialog open={showAddIssue} onOpenChange={setShowAddIssue}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Issue Stock</DialogTitle>
            <DialogDescription>Record stock issued for {selectedDate}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Select Item</Label>
              <Select value={`${issueForm.item_type}|${issueForm.item_id}`} onValueChange={(v) => {
                const [type, id] = v.split('|');
                setIssueForm({ ...issueForm, item_type: type, item_id: id });
              }}>
                <SelectTrigger><SelectValue placeholder="Select item" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_header_consumables" disabled>-- Consumables (Needs Acknowledgment) --</SelectItem>
                  {allItems.price_master_items.map(i => (
                    <SelectItem key={i.id} value={`price_master|${i.id}`}>{i.item_name} ({i.super_category})</SelectItem>
                  ))}
                  <SelectItem value="_header_na" disabled>-- Non-Consumables (Asset Register) --</SelectItem>
                  {allItems.na_price_master_items.map(i => (
                    <SelectItem key={i.id} value={`na_price_master|${i.id}`}>{i.item_name} ({i.super_category})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Quantity</Label>
              <Input type="number" step="0.01" value={issueForm.quantity} onChange={(e) => setIssueForm({ ...issueForm, quantity: e.target.value })} />
            </div>
            <div>
              <Label>Issued To (optional)</Label>
              <Input value={issueForm.issued_to} onChange={(e) => setIssueForm({ ...issueForm, issued_to: e.target.value })} placeholder="e.g., Kitchen, Bar, Housekeeping" />
            </div>
            <div>
              <Label>Remarks (optional)</Label>
              <Input value={issueForm.remarks} onChange={(e) => setIssueForm({ ...issueForm, remarks: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddIssue(false)}>Cancel</Button>
            <Button onClick={handleAddIssue} disabled={!issueForm.item_id || !issueForm.quantity}>Issue Stock</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
